import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, firstValueFrom, Observable, throwError } from "rxjs";
import { AttachMeta } from "./upload.service";
import { AlertMessage, EventMessageBusService } from "../../../public/services/event-message-bus.service";

@Injectable({
  providedIn: 'root',
})
export class ApplicationHttpService {

    constructor(private http: HttpClient, private messageService: EventMessageBusService) {
    }

    private handleError(url: string, error: any, showAdditionalModal: boolean = false): Observable<never> {
        console.error(`GET ${url} failed`, error);

        if (error instanceof HttpErrorResponse) {
            let errorPayload: any = {};

            if (error.error) {
                if (typeof error.error === 'string') {
                    errorPayload = { message: error.error };
                } else if (typeof error.error === 'object') {
                    errorPayload = error.error;
                }
            } else {
                errorPayload = { message: error.message };
            }

            console.log('Parsed backend error:', errorPayload);

            this.sendErrorMessage(errorPayload, showAdditionalModal);
            return throwError(() => errorPayload);
        }

        console.log('Unknown error:', error?.message);
        this.sendErrorMessage(error?.message || error?.error_description || 'Unknown error', showAdditionalModal);
        return throwError(() => ({ message: error?.message || 'Unknown error' }));
    }

    private sendErrorMessage(text: string, showAdditionalModal: boolean = false) {
        let message = {
            text: text,
            type: 'error',
            alertOptions: {
                timeout: 5000,
                closable: true
            },
            showAdditionalModal: showAdditionalModal
        } as AlertMessage;
        this.messageService.postMessage(message);
    }



    public get<T>(url: string, params?: any): Observable<T> {
        if (params) {
            params = `?${params}`;
        }
        else if (params === undefined) {
            params = '';
        }
        return this.http.get<T>(`${url}${params}`, ).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        );
    }

    public get2<T>(url: string, params?: any): Observable<T> {
        if (params) {
            return this.http.get<T>(`${url}?${params}`, ).pipe(
                catchError((error: any) => {
                    return this.handleError(url, error);
                })
            );
        }
        else {
            return this.http.get<T>(`${url}`,).pipe(
                catchError((error: any) => {
                    return this.handleError(url, error);
                })
            );
        }
    }

    public post<T>(url: string, body: any, params?: any, options?: any): Observable<T> {
        let _url = params ? `${url}?${params}` : `${url}`;
        const httpOptions = options || {};
        return this.http.post(_url, body, httpOptions).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        ) as Observable<T>;
    }

    public put<T>(url: string, body: any, params?: any): Observable<T> {
        let _url = params ? `${url}?${params}` : `${url}`;
        return this.http.put<T>(_url, body).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        );
    }


    public patch<T>(url: string, body: any, showAdditionalModal: boolean = true): Observable<T> {
        return this.http.patch<T>(`${url}`, body).pipe(
            catchError((error: any) => {
                return this.handleError(url, error, showAdditionalModal);
            })
        );
    }

    public delete<T>(url: string): Observable<T> {
        return this.http.delete<T>(`${url}`).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        );
    }

    public getFile(url: string): Observable<Blob> {
        return this.http.get(`${url}`, {responseType: 'blob'}).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        );
    }

    public sendFile(url: string, base64: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'text/plain' // Или application/json, если нужно
          });
        return this.http.post(`${url}`, base64, {headers}).pipe(
            catchError((error: any) => {
                return this.handleError(url, error);
            })
        )
    }

    public async download(fileUrl: string, meta: AttachMeta) {
        const response = await firstValueFrom(this.http.get(fileUrl, { responseType: 'blob' }));
        if (response) {
            const blob = new Blob([response], { type: meta.contentType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = meta.fileName;
            link.click();

            window.URL.revokeObjectURL(url);
        }
    }

    public async downloadApplicationPdf(fileUrl: string) {
        const response = await firstValueFrom(this.http.get(fileUrl, { responseType: 'blob' }));
        if (response) {
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = "Application.pdf";
            link.click();

            window.URL.revokeObjectURL(url);
        }
    }
}
